import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-background-50/95 backdrop-blur-md'
            : 'bg-transparent'
        }`}
      >
        <div className="px-4 md:px-10 py-4 flex items-center justify-between">
          {/* Brand */}
          <a href="/" className="font-heading text-xl md:text-2xl font-bold text-foreground-950 whitespace-nowrap">
            SUPERBUYSPREAD
          </a>

          {/* Center: Category Dropdown (Desktop) */}
          <div ref={dropdownRef} className="relative hidden md:block">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-2 px-6 py-3 bg-foreground-900 text-background-50 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-foreground-800 transition-colors"
            >
              All Categories
              {isDropdownOpen ? <i className="ri-arrow-up-s-line text-base"></i> : <i className="ri-arrow-down-s-line text-base"></i>}
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 bg-background-50 rounded-2xl p-5 min-w-[560px] shadow-lg border border-background-200/70 z-50">
                <div className="grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-accent-100/60 transition-colors group cursor-pointer"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-right-up-line text-accent-700 text-sm"></i>
                      </div>
                      <div className="text-left">
                        <span className="text-sm font-semibold text-foreground-900 block group-hover:text-foreground-950 transition-colors">
                          {cat.name}
                        </span>
                        <span className="text-xs text-foreground-500">
                          {cat.count}
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 bg-background-50 border border-foreground-200 text-foreground-900 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-50 transition-colors"
            >
              Superbuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 bg-foreground-950 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800 transition-colors"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer bg-foreground-950 text-background-50"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <i className="ri-close-line text-xl"></i>
            ) : (
              <i className="ri-menu-line text-xl"></i>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>

          {/* Menu Panel */}
          <div className="absolute top-0 right-0 w-full max-w-[360px] h-full bg-background-50 overflow-y-auto">
            {/* Close Button */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-background-200/70">
              <span className="font-heading text-lg font-bold text-foreground-950">Menu</span>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer bg-foreground-100 text-foreground-700 hover:bg-foreground-200 transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* CTAs at top */}
            <div className="px-4 pt-5 pb-4 flex flex-col gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full py-3 bg-foreground-950 text-background-50 rounded-full text-sm font-semibold text-center whitespace-nowrap cursor-pointer hover:bg-foreground-800 transition-colors"
              >
                Superbuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full py-3 bg-accent-500 text-foreground-950 rounded-full text-sm font-semibold text-center whitespace-nowrap cursor-pointer hover:bg-accent-400 transition-colors"
              >
                Buy It Now
              </a>
            </div>

            {/* Quick Links */}
            <div className="px-4 pb-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-3 px-1">Quick Links</p>
              <div className="space-y-1">
                {[
                  { label: 'Home', to: '/' },
                  { label: 'Complete Guide', to: '/superbuy-spreadsheet-complete-guide' },
                  { label: 'Safety Guide', to: '/superbuy-spreadsheet-safety-guide' },
                  { label: 'Beginner Tutorial', to: '/superbuy-spreadsheet-beginner-tutorial' },
                ].map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-accent-50/60 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-file-text-line text-accent-700 text-sm"></i>
                    </div>
                    <span className="text-sm font-semibold text-foreground-900">{link.label}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Categories */}
            <div className="px-4 pb-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-3 px-1">
                All Categories ({categories.length})
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex flex-col gap-1 px-3 py-3 rounded-xl bg-background-100 hover:bg-accent-50/60 transition-colors cursor-pointer"
                  >
                    <span className="text-xs font-semibold text-foreground-900 leading-tight">
                      {cat.name}
                    </span>
                    <span className="text-[10px] text-foreground-400">{cat.count}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}